/**
 * API client.
 *
 * One place where every request is built, so the access token, error shape and
 * query-string handling are consistent. The Base44 build called the SDK from
 * roughly sixty scattered sites with four different error-handling styles, and
 * about a third of them had none at all — a failed call left a spinner turning
 * forever.
 */

import { clearSession, getAccessToken, isSessionOver, refresh } from './auth-client.js';

export class ApiError extends Error {
  constructor(status, message, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }

  /** Field-level messages, when the server sent them. */
  get fieldErrors() {
    return this.details?.fields || null;
  }
}

function buildUrl(path, params) {
  const url = new URL(path, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.pathname + url.search;
}

/**
 * An access token lasts fifteen minutes, so a request can arrive just after one
 * expires. Rather than making every caller think about that, a 401 triggers one
 * refresh and one retry.
 *
 * The refresh itself is single-flight inside auth-client.js: several queries
 * failing at once produce one rotation, not several. Several would look to the
 * server like a replayed refresh token and take the whole session down.
 *
 * Retrying re-enters this function with the original arguments, which rebuilds
 * `init` and the headers from scratch — including a token that is now fresh.
 * A `FormData` body is passed through unchanged, which is safe: fetch
 * serialises it per call rather than consuming it, so an upload retries fine.
 */
async function request(method, path, options = {}, retried = false) {
  const { body, params, signal, headers = {} } = options;
  const token = await getAccessToken();

  const init = {
    method,
    signal,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  };

  if (body instanceof FormData) {
    // Let the browser set the multipart boundary.
    init.body = body;
  } else if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(buildUrl(path, params), init);
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new ApiError(0, 'Could not reach the server. Check your connection and try again.');
  }

  if (response.status === 401 && !retried && !path.startsWith('/api/auth/')) {
    const outcome = await refresh().then(
      () => 'recovered',
      (error) => (isSessionOver(error) ? 'over' : 'unreachable'),
    );

    if (outcome === 'recovered') return request(method, path, options, true);
    if (outcome === 'unreachable') {
      // Could not renew, but nothing said the session is over — keep it.
      throw new ApiError(0, 'Could not reach the server. Check your connection and try again.');
    }

    // The session is genuinely over. Clearing it flips the auth context to
    // signed-out, so the shell shows the sign-in screen instead of every query
    // on the page reporting its own error.
    clearSession();
  }

  if (response.status === 204) return null;

  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error || `Request failed (${response.status})`,
      payload?.details,
    );
  }

  return payload;
}

export const api = {
  get: (path, options) => request('GET', path, options),
  post: (path, body, options) => request('POST', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  delete: (path, options) => request('DELETE', path, options),

  /** Upload a single file and return `{ url, path, bucket }`. */
  async upload(kind, file) {
    const form = new FormData();
    form.append('file', file);
    return request('POST', `/api/uploads/${kind}`, { body: form });
  },

  /**
   * Fetch a binary response (the PO document) as a blob.
   * The caller is responsible for revoking any object URL it creates.
   */
  async blob(path, params, retried = false) {
    const token = await getAccessToken();
    const response = await fetch(buildUrl(path, params), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    // Same expiry window as every other call, so the same one retry.
    if (response.status === 401 && !retried) {
      const recovered = await refresh().then(
        () => true,
        () => false,
      );
      if (recovered) return api.blob(path, params, true);
      clearSession();
    }

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new ApiError(response.status, payload?.error || 'Could not load the document.');
    }

    return response.blob();
  },
};

/** Turn any thrown value into something worth showing a person. */
export function errorMessage(error, fallback = 'Something went wrong.') {
  if (!error) return fallback;
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message || fallback;
  return String(error) || fallback;
}
